import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface Institute {
  institute_id: string;
  name: string;
  address: string;
  contact_no: string;
  email: string;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  data: Institute[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const InstituteProfile: React.FC = () => {
  const [institute, setInstitute] = useState<Institute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInstitute();
  }, []);

  const fetchInstitute = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get<ApiResponse>('/v1/institutes');
      
      if (response.data.success && response.data.data.length > 0) {
        setInstitute(response.data.data[0]);
      } else {
        setError('No institute found for this account');
      }
    } catch (err: any) {
      console.error('Failed to fetch institute:', err);
      setError(err.response?.data?.error || 'Failed to load institute details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '60px', height: '60px' }} 
            strokeWidth="4"
            animationDuration="1s"
          />
          <p style={{
            marginTop: '20px',
            color: '#0f3d3e',
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Loading institute details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        borderRadius: '12px'
      }}>
        <Message 
          severity="error" 
          text={error}
          style={{ 
            width: '100%', 
            justifyContent: 'center',
            marginBottom: '20px'
          }}
        />
        <div style={{ textAlign: 'center' }}>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            onClick={fetchInstitute}
            severity="danger"
            outlined
          />
        </div>
      </div>
    );
  }

  if (!institute) {
    return (
      <div style={{
        padding: '40px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <Message 
          severity="warn" 
          text="No institute information available"
          style={{ width: '100%', justifyContent: 'center' }}
        />
      </div>
    );
  }

  const header = (
    <div style={{
      background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
      padding: '20px',
      borderRadius: '12px 12px 0 0',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    }}>
      <div style={{
        width: '45px',
        height: '45px',
        background: '#d4f17e',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
      }}>
        <i className="pi pi-building" style={{ fontSize: '1.4rem', color: '#0f3d3e' }}></i>
      </div>
      <div>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '1.35rem', 
          fontWeight: '700',
          letterSpacing: '-0.5px'
        }}>
          {institute.name}
        </h2>
        <p style={{ 
          margin: 0, 
          opacity: 0.9,
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className="pi pi-calendar" style={{ fontSize: '0.75rem' }}></i>
          Established: {formatDate(institute.created_at)}
        </p>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh'
    }}>
      <Card 
        header={header}
        style={{
          margin: '0 auto',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        <div style={{ padding: '0px 16px' }}>
          {/* Contact Information Section */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              color: '#0f3d3e',
              fontSize: '0.9rem',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="pi pi-info-circle" style={{ color: '#c9f445ff', fontSize: '0.85rem' }}></i>
              Contact Information
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '12px'
            }}>
              {/* Email */}
              <div style={{
                padding: '14px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '6px'
                }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    background: '#d4f17e',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="pi pi-envelope" style={{ color: '#0f3d3e', fontSize: '0.85rem' }}></i>
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#64748b',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Email</span>
                </div>
                <p style={{
                  margin: '0',
                  color: '#0f3d3e',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  wordBreak: 'break-word'
                }}>
                  {institute.email}
                </p>
              </div>

              {/* Phone */}
              <div style={{
                padding: '14px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '6px'
                }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    background: '#d4f17e',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="pi pi-phone" style={{ color: '#0f3d3e', fontSize: '0.85rem' }}></i>
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#64748b',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Phone</span>
                </div>
                <p style={{
                  margin: '0',
                  color: '#0f3d3e',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  {institute.contact_no}
                </p>
              </div>
            </div>
          </div>

          <Divider />

          {/* Address Section */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={{
              color: '#0f3d3e',
              fontSize: '0.9rem',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="pi pi-map-marker" style={{ color: '#d4f17e', fontSize: '0.85rem' }}></i>
              Location
            </h3>

            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '34px',
                height: '34px',
                background: '#d4f17e',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <i className="pi pi-map" style={{ color: '#0f3d3e', fontSize: '0.95rem' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  margin: '0',
                  color: '#0f3d3e',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  lineHeight: '1.5'
                }}>
                  {institute.address}
                </p>
              </div>
            </div>
          </div>

          {/* Institute ID (hidden badge) */}
          <div style={{ 
            marginTop: '20px',
            padding: '12px',
            background: 'rgba(15, 61, 62, 0.05)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-id-card" style={{ color: '#64748b', fontSize: '0.85rem' }}></i>
              <span style={{
                fontSize: '0.75rem',
                color: '#64748b',
                fontWeight: '500'
              }}>
                Institute ID
              </span>
            </div>
            <code style={{
              padding: '4px 10px',
              background: 'white',
              borderRadius: '6px',
              fontSize: '0.75rem',
              color: '#0f3d3e',
              fontWeight: '500',
              border: '1px solid #e2e8f0'
            }}>
              {institute.institute_id}
            </code>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InstituteProfile;